import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  useMemo,
} from "react";

import { Platform } from "react-native";
import { Device } from "@twilio/voice-sdk";

import Engage from "~/services/engage";
import { TWILIO_VOICE_OPTIONS } from "~/constants/twilio";
import { callReducer, INIT_CALL_STATE } from "~/reducers/callReducer";

export default function useTwilioInit(identity) {
  const device = useRef(null);

  const [ready, setReady] = useState(false);
  const [token, setToken] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [eventCallData, setEventCallData] = useState(null);

  const [callState, dispatch] = useReducer(callReducer, INIT_CALL_STATE);

  const callStatus = useMemo(() => {
    if (callState.failure) {
      return "Call failed";
    }
    if (callState.disconnected) {
      return "Call ended";
    }
    if (callState.ringing) {
      return "Ringing";
    }
    return "";
  }, [callState]);

  /**
   *
   *
   * @param {*}
   * @return {*}
   */
  const updateActiveCall = useCallback(
    (call) => {
      if (!activeCall) {
        return;
      }

      // Don't allow these properties to be changed on an existed call.
      const { destination, direction, ...data } = call;
      setActiveCall({
        ...activeCall,
        ...data,
      });
    },
    [activeCall]
  );

  useEffect(() => {
    if (eventCallData) {
      updateActiveCall(eventCallData);
    }
  }, [eventCallData]);

  const getAccessToken = useCallback(async () => {
    const res = await Engage.getTwilioAccessToken(identity, Platform.OS);
    setToken(res?.token);
    return res?.token;
  }, [setToken, identity]);

  const initialize = useCallback(async () => {
    const accessToken = await getAccessToken();
    if (accessToken) {
      console.log("{ initialize }");
      device.current = new Device(accessToken, TWILIO_VOICE_OPTIONS);
      setReady(true);
    }
  }, [getAccessToken, setReady]);

  useEffect(() => {
    if (identity) {
      initialize();

      return () => {
        device.current?.destroy();
        device.current = null;
      };
    }
  }, [identity]);

  useEffect(() => {
    if (ready && device.current) {
      device.current.addListener("registering", (e) => {
        console.log("[useTwilioInit:Registrating]", e);
      });

      device.current.addListener("registered", (e) => {
        console.log("[useTwilioInit:RegistrationSuccess]", e);
      });

      device.current.addListener("unregistered", (e) => {
        console.log("[useTwilioInit:Unregistered]", e);
        setActiveCall(null);
        dispatch({ type: "FAIL" });
      });

      device.current.addListener("incoming", (call) => {
        console.log("[useTwilioInit:CallInvite]", call);

        const onAccept = (acceptedCall) => {
          console.log("[incoming_call:CallAccepted]", acceptedCall);
          setActiveCall(acceptedCall);
          dispatch({ type: "CONNECTED" });
        };

        const onDisconnect = (disconnectedCall) => {
          console.log("[incoming call:CallDisconnected]", disconnectedCall);
          if (disconnectedCall) {
            disconnectedCall.removeAllListeners();
            disconnectedCall.disconnect();
            setActiveCall(null);
          }
          dispatch({ type: "DISCONNECTED" });
        };

        const onReject = (rejectedCall) => {
          console.log("[incoming call:CallRejected]", rejectedCall);
          setActiveCall(null);
          dispatch({ type: "DISCONNECTED" });
        };

        const onCancel = (rejectedCall) => {
          console.log("[incoming call:CallCancel]", rejectedCall);
          setActiveCall(null);
          dispatch({ type: "DISCONNECTED" });
        };

        const onError = (rejectedCall) => {
          console.log("[incoming call:CallError]", rejectedCall);
          setActiveCall(null);
          dispatch({ type: "DISCONNECTED" });
        };

        call.on("reject", onReject);
        call.on("cancel", onCancel);
        call.on("error", onError);
        call.on("accept", onAccept);
        call.on("disconnect", onDisconnect);

        dispatch({ type: "INVITED" });

        setActiveCall(call);
      });

      device.current.on("error", (e, callInstance) => {
        console.error("[useTwilioInit:Error]", e, callInstance);
        if (callInstance) {
          callInstance.removeAllListeners();
          callInstance.disconnect();
        }
        setActiveCall(null);
      });

      device.current.register();
    }

    return () => {
      if (device.current) {
        device.current.removeAllListeners();
      }
    };
  }, [ready, device.current, dispatch]);

  const startDeviceCall = useCallback(
    async ({ to, from }) => {
      if (!ready) {
        console.warn("Attempted to place call before device was ready!");
        return;
      }

      if (activeCall && !callState.invited) {
        console.warn("Attempted to place multiple calls!");
        return;
      }

      let call;

      if (activeCall && callState.invited) {
        call = activeCall;
      } else {
        call = await device.current.connect({
          params: { to, To: to, from, from },
        });

        const onAccept = (acceptedCall) => {
          console.log("[call:CallAccepted]", acceptedCall);
          setActiveCall(acceptedCall);
          dispatch({ type: "CONNECTED" });
        };

        const onReject = (rejectedCall) => {
          console.log("[call:CallRejected]", rejectedCall);
          setActiveCall(null);
          dispatch({ type: "DISCONNECTED" });
        };

        const onCancel = (rejectedCall) => {
          console.log("[call:CallCancel]", rejectedCall);
          rejectedCall.disconnect();
          setActiveCall(null);
          dispatch({ type: "DISCONNECTED" });
        };

        const onError = (rejectedCall) => {
          console.log("[call:CallError]", rejectedCall);
          setActiveCall(null);
          dispatch({ type: "DISCONNECTED" });
        };

        const onDisconnect = (disconnectedCall) => {
          console.log("[call:CallDisconnected]", disconnectedCall);

          disconnectedCall.removeAllListeners();
          disconnectedCall.disconnect();
          setActiveCall(null);
          dispatch({ type: "DISCONNECTED" });
        };

        const onMute = () => {
          console.log("[call:CallMuted]");
        };

        call.on("mute", onMute);
        call.on("accept", onAccept);
        call.on("reject", onReject);
        call.on("cancel", onCancel);
        call.on("error", onError);
        call.on("disconnect", onDisconnect);
      }

      if (callState.invited) {
        call.accept();
        // setActiveCall(call);
        // console.log(JSON.stringify(call.customParameters, null, 2));
        // dispatch({ type: "CONNECTED" });
      } else {
        setActiveCall(call);
        dispatch({ type: "RING" });
      }
    },
    [ready, callState, activeCall, dispatch, setActiveCall]
  );

  const endDeviceCall = useCallback(() => {
    if (!device.current && !ready) {
      console.warn("Attempting to end call with no device or not ready");
    }
    if (!activeCall) {
      console.warn("Attempting to end call with no activeCall ");
    }

    if (activeCall && callState.invited && !callState.success) {
      activeCall.reject();
    } else {
      device.current.disconnectAll();
    }
  }, [ready, activeCall, callState]);

  // useEffect(() => {
  //   if (ready && device) {
  //     // Now we have a live input audio track, opened with echoCancellation:true
  //     device.audio
  //       .setAudioConstraints({
  //         echoCancellation: true,
  //       })
  //       .then(
  //         () => {
  //           device.audio.setInputDevice("default");
  //         },
  //         async (err) => {
  //           // Something went wrong, most likely err is an OverconstrainedError. Let's roll back.
  //           await device.audio.unsetAudioConstraints();
  //           // We should now have a working input audio track again
  //         }
  //       );
  //   }
  // }, [ready, device]);

  return {
    ready,
    device: device.current,
    dispatch,
    activeCall,
    startDeviceCall,
    endDeviceCall,
    callState,
    token,
    callStatus,
  };
}
